/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {isCirus, isDickeyJohn, isForceAmerica, isRoadWatch} from '@ion/core/src/entities/Manufacturer';
import {GenericObject} from '@ion/core/src/types/entities';
import {getCirusLayout} from './cirusLayout';
import {getDickeyJohnLayout} from './dickeyJohnLayout';
import {getForceAmericaLayout} from './forceAmericaLayout';
import {SpreaderLayout} from './spreaderLayoutShared';

/**
 * Maps a spreader message's measures onto the current theme Spreader
 * accordion's layout for its manufacturer: Force America (Type 1 or
 * Type 2, per `isForceAmericaTypeOne`), Dickey-john (Type 2) or Cirus /
 * RoadWatch (Type 3). Readings are formatted by the same `spreaderUtils`
 * formatters legacy uses, a missing reading reads `N/A`, and every reading
 * legacy shows that the design has no slot for is still shown - in a
 * Conditions, Totals or Other Details card.
 *
 * ```typescript
 *
 *   import {getSpreaderLayout} from './spreaderCurrentLayouts';
 *   ...
 *
 *   const layout = getSpreaderLayout(sensorType, measures);
 *
 * ```
 *
 * @param   sensorType The spreader's manufacturer.
 * @param   measures   The spreader message's measures.
 * @returns            The accordion layout, or `null` for an unknown
 *                     manufacturer.
 */
export const getSpreaderLayout = (sensorType: string, measures: GenericObject): SpreaderLayout | null => {
  let layout: SpreaderLayout | null = null;
  if (isForceAmerica(sensorType)) {
    layout = getForceAmericaLayout(measures);
  } else if (isDickeyJohn(sensorType)) {
    layout = getDickeyJohnLayout(measures);
  } else if (isCirus(sensorType) || isRoadWatch(sensorType)) {
    layout = getCirusLayout(measures);
  }
  return layout;
};
